import { useContext, useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router';
import { createBooking, searchAvailability } from '../apis/hotelApis';
import { DateTimeFormats, language, PaymentMethod, PHONE_CHARS, PHONE_REGEX } from '../common/constants';
import { Loader } from '../controls/Loader';
import IconBack from '../icons/IconBack';
import { RoomSearchResult } from '../modules/RoomSearchResult';
import { ISearchQueryParams } from '../types/bookingFormTypes';
import { ICreateBookingRequest, IHotelAvailability } from '../types/hotelTypes';
import { getCheckoutDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/numberUtil';
import { formatDuration } from '../utils/stringUtils';
import { getPageUrl, getQueryParams, searchUrl } from '../utils/urlUtils';
import {
  StyledBreadcrumb,
  StyledCard,
  StyledContactForm,
  StyledDetailText,
  StyledReview,
  StyledReviewActions,
  StyledCardTitle,
  StyledSummary,
  StyledReviewButtonText,
  StyledReviewOriginalPrice,
} from './Review.styled';
import { StyledHotelName } from './Search.styled';
import { FormProvider, useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet';
import { useNewRelic } from '../hooks/useNewRelic';
import { HotelContext } from '../modules/HotelContext';
import { useScrollIntoViewIframe } from '../hooks/useScrollIntoView';
import IconSuccess from '../icons/IconSuccess';
import { PaymentForm } from '../modules/PaymentForm';
import { useQuery } from '@tanstack/react-query';
import { object, string } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { DateTime } from 'luxon';
import { getDepositAmountVnd } from '../utils/paymentOptionUtils';

declare global {
  interface Window {
    fbq?: (
      event: string,
      eventName: string,
      params?: {
        value?: number;
        currency?: string;
        [key: string]: unknown;
      },
    ) => void;
  }
}

interface IFormState {
  phoneNumber: string;
  contactName: string;
  note: string;
  paymentMethod: PaymentMethod;
}

const schema = object({
  phoneNumber: string().required().matches(PHONE_REGEX),
  contactName: string().required().max(50),
  paymentMethod: string().required(),
});

export const Review = () => {
  const { hotelId, roomTypeId } = useParams();
  const { hotel, source } = useContext(HotelContext);
  const { search, state } = useLocation();
  const navigate = useNavigate();
  const query = getQueryParams<ISearchQueryParams>(search);
  const [bookingError, setBookingError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const depositAmountVnd = getDepositAmountVnd(hotel.paymentOptions);
  const reviewForm = useForm<IFormState>({
    mode: 'all',
    defaultValues: {
      phoneNumber: '',
      contactName: '',
      note: '',
      paymentMethod: null,
    },
    resolver: yupResolver(schema) as never,
  });
  const {
    handleSubmit,
    register,
    watch,
    formState: { errors, touchedFields },
  } = reviewForm;

  const selectedPaymentMethod = watch('paymentMethod');

  if (!hotelId || !roomTypeId) {
    navigate('/');
    return null;
  }

  const from = DateTime.fromISO(query.from);
  const to = getCheckoutDate(from, { ...query, overnightTimeFrom: hotel.overnightTimeFrom });

  const { data = state?.data as IHotelAvailability[] } = useQuery({
    queryKey: [`/api/hotels/${hotelId}/availability`, query.from, query.hours, query.nights, query.priceOption],
    queryFn: () => {
      return searchAvailability(hotelId, {
        from: from.toFormat(DateTimeFormats.GENERIC_DATE_FORMAT),
        to: to.toFormat(DateTimeFormats.GENERIC_DATE_FORMAT),
        priceOption: query.priceOption,
      });
    },
  });

  const goBack = () => {
    navigate(`${searchUrl()}/${hotelId}${search}`);
  };

  useNewRelic(hotel);
  useScrollIntoViewIframe(Boolean(data));

  if (!data) {
    return <Loader />;
  }

  const room = hotel.rooms.find((item) => item.roomTypeId === Number(roomTypeId));
  if (!room) {
    navigate('/');
    return null;
  }

  const priceInfo = data.find((item) => item.RoomTypeId === Number(roomTypeId));
  if (!priceInfo?.RoomPriceId) {
    return <Alert variant="danger">{language.GENERIC_ERROR}</Alert>;
  }

  const onSubmit = async ({ note, contactName, phoneNumber, paymentMethod }: IFormState) => {
    setBookingError('');
    setIsSubmitting(true);
    try {
      const req: ICreateBookingRequest = {
        from: from.toFormat(DateTimeFormats.GENERIC_DATE_FORMAT),
        to: to.toFormat(DateTimeFormats.GENERIC_DATE_FORMAT),
        note,
        contactName,
        phoneNumber,
        roomTypeId: room.roomTypeId,
        roomPriceId: priceInfo?.RoomPriceId,
        emailInfo: {
          priceInfo,
        },
        priceOption: query.priceOption,
        paymentMethod: paymentMethod,
        source,
      };
      const response = await createBooking(hotelId, req);

      // Track purchase event
      if (window.fbq) {
        window.fbq('track', 'Purchase', {
          value: priceInfo.TotalPrice,
          currency: 'VND',
        });
      }

      navigate(getPageUrl(`/${hotelId}/reservation/${response.Id}`), {
        state: { priceInfo, paymentMethod },
      });
    } catch (error) {
      console.error(error);
      setBookingError(language.GENERIC_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StyledReview fluid>
      <Helmet>
        <title>{`Nhập thông tin đặt phòng - ${hotel?.hotelName}`}</title>
      </Helmet>
      {isSubmitting && <Loader fullScreen />}
      <StyledBreadcrumb>
        <div className="left-breadcrumb">
          <Button size="sm" variant="link" onClick={goBack}>
            <IconBack />
          </Button>
          <StyledHotelName>{hotel.hotelName}</StyledHotelName>
        </div>
        <Button size="sm" variant="link" onClick={goBack}>
          Thay đổi tìm kiếm
        </Button>
      </StyledBreadcrumb>
      {bookingError && <Alert variant="danger">{bookingError}</Alert>}
      {priceInfo?.AvailableRooms <= 0 && (
        <Alert variant="danger">Rất tiếc, đã hết phòng vào thời gian được chọn, vui lòng tìm kiếm lại.</Alert>
      )}
      <Row>
        <Col sm={12} md={6}>
          <StyledCard>
            <RoomSearchResult isLoading={!data} roomInfo={room} />
          </StyledCard>
        </Col>
        {priceInfo?.AvailableRooms > 0 && (
          <>
            <Col sm={12} md={6}>
              <StyledContactForm>
                <StyledCardTitle>Nhập thông tin đặt phòng của bạn</StyledCardTitle>
                <Form.Group className="mb-2" controlId="phoneNumber">
                  <Form.Label className="required">Số điện thoại</Form.Label>
                  <Form.Control
                    {...register('phoneNumber', {
                      pattern: PHONE_REGEX,
                      required: true,
                    })}
                    type="tel"
                    className={touchedFields.phoneNumber && errors.phoneNumber ? 'is-invalid' : ''}
                    isValid={touchedFields.phoneNumber ? !errors.phoneNumber : undefined}
                    onKeyDown={(e) => {
                      if (!(e.ctrlKey || e.metaKey) && !PHONE_CHARS.test(e.key) && e.key.length === 1) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <Form.Text muted>Vui lòng cung cấp sđt gọi được để Đào Tiên gọi điện xác nhận đặt phòng.</Form.Text>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="required">Tên liên hệ</Form.Label>
                  <Form.Control
                    {...register('contactName', {
                      required: true,
                    })}
                    className={touchedFields.contactName && errors.contactName ? 'is-invalid' : ''}
                    isValid={touchedFields.contactName ? !errors.contactName : undefined}
                    name="contactName"
                    maxLength={50}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Các yêu cầu đặc biệt</Form.Label>
                  <Form.Control as="textarea" {...register('note')} />
                </Form.Group>
              </StyledContactForm>
            </Col>
            <Col sm={12}>
              <FormProvider {...reviewForm}>
                <PaymentForm paymentOptions={hotel.paymentOptions} webPaymentServiceId={hotel.webPaymentServiceId} />
              </FormProvider>
            </Col>
          </>
        )}
        <Col sm={12}>
          <StyledCard>
            <StyledCardTitle>Chi tiết đặt phòng của bạn</StyledCardTitle>
            <StyledDetailText>
              <span>Nhận phòng: </span>
              <b>{from.toFormat('HH:mm, cccc dd/MM/yyyy')}</b>
            </StyledDetailText>
            <StyledDetailText>
              <span>Trả phòng: </span>
              <b>{to.toFormat('HH:mm, cccc dd/MM/yyyy')}</b>
            </StyledDetailText>
            <StyledDetailText>
              <span>Tổng thời gian lưu trú: </span>
              <b>{formatDuration(query)}</b>
            </StyledDetailText>
            {priceInfo && (
              <>
                <StyledDetailText>
                  <span>{room.roomTypeName}: </span>
                  <b>{formatCurrency(priceInfo.TotalPrice)}</b>
                </StyledDetailText>
                {selectedPaymentMethod === PaymentMethod.SecurityDeposit && (
                  <>
                    <StyledSummary>
                      <div>Tổng giá phòng</div>
                      <div>
                        {formatCurrency(priceInfo.TotalPrice)}
                        {priceInfo.TotalOriginalPrice > priceInfo.TotalPrice && (
                          <StyledReviewOriginalPrice>
                            {formatCurrency(priceInfo.TotalOriginalPrice)}
                          </StyledReviewOriginalPrice>
                        )}
                      </div>
                    </StyledSummary>
                    <StyledSummary>
                      <div>Tiền đặt cọc</div>
                      <div>
                        <b>{formatCurrency(depositAmountVnd)}</b>
                      </div>
                    </StyledSummary>
                    <StyledSummary>
                      <div>
                        Còn lại
                        <div className="price-subtext">(thanh toán khi nhận phòng)</div>
                      </div>
                      <div>{formatCurrency(priceInfo.TotalPrice - depositAmountVnd)}</div>
                    </StyledSummary>
                  </>
                )}
                {selectedPaymentMethod !== PaymentMethod.SecurityDeposit && (
                  <StyledSummary>
                    <div>
                      Giá
                      <div className="price-subtext">(đã bao gồm thuế và phí)</div>
                    </div>
                    <div>
                      <b>{formatCurrency(priceInfo.TotalPrice)}</b>
                      {priceInfo.TotalOriginalPrice > priceInfo.TotalPrice && (
                        <StyledReviewOriginalPrice>
                          {formatCurrency(priceInfo.TotalOriginalPrice)}
                        </StyledReviewOriginalPrice>
                      )}
                    </div>
                  </StyledSummary>
                )}
              </>
            )}
          </StyledCard>
        </Col>
      </Row>
      {priceInfo?.AvailableRooms > 0 && (
        <StyledReviewActions className="d-grid">
          <Button size="lg" onClick={handleSubmit(onSubmit)}>
            <IconSuccess />
            <StyledReviewButtonText className="ml-1">Nhấn để xác nhận đặt phòng</StyledReviewButtonText>
          </Button>
        </StyledReviewActions>
      )}
    </StyledReview>
  );
};
